import { StringUtils } from '~/utils/string-utils';
import { validationMessages, ValidationRules } from 'aurelia-validation';
import moment from 'moment';
import { DateFormatValueConverter } from '~/converters/date-format';
import { FieldValidation, ValidationRuleName } from '~/models/field-validation';
import { Conditional } from 'aurelia-framework';

/**
 * Defines all custom validation rules which are stored in FluentRules.customRules
 * Each custom rules requires:
 * - Rule name: string
 * - Condition: Function
 * - Validation message: string
 *
 * Optional config parameter is mainly used by validation message.
 */

export enum CustomValidationRuleName {
	NUMERIC_RANGE = 'numericRange',
	DECIMAL_DIGITS = 'decimalPrecision',
	REQUIRED = 'required',
	VALID_DATE = 'validDate',
}

export enum CustomValidationMessage {
	NUMBER_OUT_OF_RANGE = 'validation-errors.number-out-of-range',
	EXCEED_MAX_LENGTH = 'validation-errors.max-length-with-length',
	DECIMAL_DIGITS = 'validation-errors.decimal-precision',
	DATE_AFTER = 'validation-errors.date-after',
	DATE_BEFORE = 'validation-errors.date-before',
	DATE_EQUALS = 'validation-errors.date-equals',
	DATE_INVALID = 'validation-errors.date-invalid',
	REQUIRED = 'validation-errors.required',
	STRING_NOT_STARTS_WITH_UNDERSCORE = 'validation-errors.name-starting-with-underscore-are-reserved-acurity-internal-use',
}

/**
 * Applies NUMERIC_RANGE validation rule to the property.
 * null and undefined are considered valid.
 * empty-string values are also considered valid in case an empty string is set into a number validate field
 */
ValidationRules.customRule(
	CustomValidationRuleName.NUMERIC_RANGE,
	(value, obj, min, max) =>
		value === null ||
		value === undefined ||
		value.length === 0 ||
		(parseInt(value, 10) >= min && parseInt(value, 10) <= max),
	CustomValidationMessage.NUMBER_OUT_OF_RANGE,
	(min, max) => ({ min, max }),
);

/**
 * Applies DECIMAL_DIGITS validation rule to the property. A decimal string representation is invalid if its effecitive
 * digits exceeds length in the parameter.
 * null and undefined are considered valid.
 * empty-string values are also considered valid in case an empty string is set into a number validate field
 */
ValidationRules.customRule(
	CustomValidationRuleName.DECIMAL_DIGITS,
	(value, obj, length) => {
		if (value === null || value === undefined || value.length === 0) {
			return true;
		}
		value = value
			.replace(new RegExp(',', 'g'), '')
			.replace(new RegExp('\\.', 'g'), '')
			.replace(new RegExp('-', 'g'), '');
		if (value.startsWith('0')) {
			value = value.substr(1, value.length);
		}
		return value.length <= length;
	},
	CustomValidationMessage.DECIMAL_DIGITS,
	(length) => ({ length }),
);

ValidationRules.customRule(
	CustomValidationRuleName.VALID_DATE,
	(value: string | undefined | null, obj) => {
		if (value === null || value === undefined || value.length === 0) {
			return true;
		}
		// NOTE: Jake: 2019-07-22 - WEBUI-2271
		// Assume date is formatted like so: 9999-01-01T00:00:00
		const valueParts = value.split('-', 1);
		if (valueParts[0] === '9999') {
			// NOTE: Jake: 2019-07-22 - WEBUI-2271
			// Any date ending in the year 9999 is invalid.
			// If user enters an invalid date, we change it to 01/01/9999 (in datepicker.ts code)
			return false;
		}
		return true;
	},
	CustomValidationMessage.DATE_INVALID,
);

// Validation Rules from server side
{
	ValidationRules.customRule(
		ValidationRuleName.DATE_AFTER,
		(value, obj, validation: FieldValidation) => {
			const inputDate =
				DateFormatValueConverter.toAcurityApiFormat(value);
			const controlDate = DateFormatValueConverter.toAcurityApiFormat(
				validation.controlValue,
			);
			return moment(inputDate).isAfter(controlDate);
		},
		CustomValidationMessage.DATE_AFTER,
		// config.controlDate is used by validation message, see common-strings.json
		(validation: FieldValidation) => ({
			controlDate: validation.controlValue,
		}),
	);
	ValidationRules.customRule(
		ValidationRuleName.DATE_BEFORE,
		(value, obj, validation: FieldValidation) => {
			const inputDate =
				DateFormatValueConverter.toAcurityApiFormat(value);
			const controlDate = DateFormatValueConverter.toAcurityApiFormat(
				validation.controlValue,
			);
			return moment(inputDate).isBefore(controlDate);
		},
		CustomValidationMessage.DATE_BEFORE,
		// config.controlDate is used by validation message, see common-strings.json
		(validation: FieldValidation) => ({
			controlDate: validation.controlValue,
		}),
	);
	ValidationRules.customRule(
		ValidationRuleName.STRING_EQUALS,
		(value, obj, validation: FieldValidation) => {
			return value.trim() === validation.controlValue.trim();
		},
		'${$config.message}',
		(validation: FieldValidation) => ({ message: validation.message }),
	);
	ValidationRules.customRule(
		ValidationRuleName.STRING_NOT_EQUALS,
		(value, obj, validation: FieldValidation) => {
			return value.trim() !== validation.controlValue.trim();
		},
		'${$config.message}',
		(validation: FieldValidation) => ({ message: validation.message }),
	);

	ValidationRules.customRule(
		ValidationRuleName.STRING_NOT_EQUALS_OR_CONDITION,
		(value, obj, validation: FieldValidation) => {
			return (
				value.trim() !== validation.controlValue.trim() ||
				validation.condition
			);
		},
		'${$config.message}',
		(validation: FieldValidation) => ({ message: validation.message }),
	);
	ValidationRules.customRule(
		ValidationRuleName.STRING_NOT_STARTS_WITH_UNDERSCORE,
		(value) => {
			return !value.startsWith('_');
		},
		CustomValidationMessage.STRING_NOT_STARTS_WITH_UNDERSCORE,
	);
	ValidationRules.customRule(
		ValidationRuleName.STRING_MATCHES_REGEX,
		(value, obj, validation: FieldValidation) => {
			return new RegExp(validation.controlValue).test(value);
		},
		'${$config.message}',
		(validation: FieldValidation) => ({ message: validation.message }),
	);
	ValidationRules.customRule(
		ValidationRuleName.NUMBER_EQUAL_OR_LESS_THAN,
		(value, obj, validation: FieldValidation) => {
			return parseInt(value, 10) <= parseInt(validation.controlValue, 10);
		},
		'${$config.message}',
		(validation: FieldValidation) => ({ message: validation.message }),
	);
	ValidationRules.customRule(
		ValidationRuleName.NUMBER_EQUAL_OR_GREATER_THAN,
		(value, obj, validation: FieldValidation) => {
			return parseInt(value, 10) >= parseInt(validation.controlValue, 10);
		},
		'${$config.message}',
		(validation: FieldValidation) => ({ message: validation.message }),
	);
}

ValidationRules.customRule(
	CustomValidationRuleName.REQUIRED,
	(value) =>
		value !== null &&
		value !== undefined &&
		!(value.toString() === '[object String]' && !/\S/.test(value)),
	CustomValidationMessage.REQUIRED,
);
